import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { lazyLoadStore } from './lazyLoadStore';
import UsernameText from '~/components/Texts/UsernameText';
import { addEvent, removeEvent } from '../listenerHelpers';
import { stringIsEmpty, addCommasToNumber } from '../stringHelpers';
import {
  useAppContext,
  useKeyContext,
  useContentContext,
  useProfileContext
} from '~/contexts';
export { default as useScrollToBottom } from './useScrollToBottom';
export { default as useInfiniteScroll } from './useInfiniteScroll';
export { default as useAiEnergySponsorship } from './useAiEnergySponsorship';
export { default as useLiveComment } from './useLiveComment';
export { default as useDraft } from './useDraft';
export { default as useDragSort } from './useDragSort';
import {
  ADMIN_MANAGEMENT_LEVEL,
  defaultContentState,
  DEFAULT_PROFILE_THEME,
  localStorageKeys,
  wordleGuessReaction,
  wordLevelHash
} from '~/constants/defaultValues';
import { Color } from '~/constants/css';
import { levels } from '~/constants/userLevels';
import { User, UserLevel } from '~/types';
import {
  getStoredItem,
  hasExplicitAuthLogoutMarker,
  hasRejectedAuthSessionMarker
} from '~/helpers/userDataHelpers';
import { getVisibleCachedIdentity } from '~/helpers/sessionInterruption';
import { measureOwnerLayout } from '~/helpers/ownerTrace';
import { throttle } from '~/helpers';
import {
  eventTargetsPopupDismissNavigationFeedCard,
  markPopupDismissNavigationSuppressed
} from '~/helpers/popupDismissNavigation';
import {
  popupTargetIsOutside,
  type PopupBoundaryRef
} from '~/helpers/popupBoundary';

const EMPTY_PROFILE_FEEDS: any[] = [];
const EMPTY_USER_STATE = {};
const DEFAULT_PROFILE_NOTABLES = {
  feeds: EMPTY_PROFILE_FEEDS,
  loaded: false,
  loadMoreButton: false
};
const DEFAULT_PROFILE_SUBJECTS = {
  posts: EMPTY_PROFILE_FEEDS,
  loaded: false,
  loadMoreButton: false
};
const DEFAULT_PROFILE_PINNED_AI_CARDS = {
  cardIds: EMPTY_PROFILE_FEEDS,
  loaded: false
};
const DEFAULT_PROFILE_PINNED_BUILDS = {
  builds: EMPTY_PROFILE_FEEDS,
  loaded: false,
  isTopBuilds: false,
  expanded: false
};
const DEFAULT_PROFILE_LIKES = {
  all: EMPTY_PROFILE_FEEDS,
  comments: EMPTY_PROFILE_FEEDS,
  subjects: EMPTY_PROFILE_FEEDS,
  videos: EMPTY_PROFILE_FEEDS,
  links: EMPTY_PROFILE_FEEDS
};
const DEFAULT_PROFILE_POSTS = {
  all: EMPTY_PROFILE_FEEDS,
  comments: EMPTY_PROFILE_FEEDS,
  subjects: EMPTY_PROFILE_FEEDS,
  videos: EMPTY_PROFILE_FEEDS,
  watched: EMPTY_PROFILE_FEEDS,
  links: EMPTY_PROFILE_FEEDS
};

interface OutsideClickListener {
  refs: PopupBoundaryRef[];
  handler: () => void;
  suppressFeedCardNavigation: boolean;
}

type OutsideClickPhase = 'bubble' | 'capture';

const outsideClickListeners: Record<
  OutsideClickPhase,
  Set<OutsideClickListener>
> = {
  bubble: new Set(),
  capture: new Set()
};
const globalOutsideHandlers: Array<{
  eventName: string;
  handler: (event: Event) => void;
  phase: OutsideClickPhase;
}> = [];

const pointerEventsSupported =
  typeof window !== 'undefined' && 'PointerEvent' in window;

function handleGlobalPointerDown(event: Event, phase: OutsideClickPhase) {
  const listeners = Array.from(outsideClickListeners[phase]);
  const target = event.target as Node | null;
  const triggeredListeners = listeners.filter((listener) =>
    popupTargetIsOutside(listener.refs, target)
  );
  if (
    triggeredListeners.some((listener) => listener.suppressFeedCardNavigation) &&
    eventTargetsPopupDismissNavigationFeedCard(event)
  ) {
    markPopupDismissNavigationSuppressed(event);
  }
  // Defer execution to avoid blocking tap/click events on iOS
  // This allows the click to reach its target first, then outside-click handlers fire
  requestAnimationFrame(() => {
    for (const listener of triggeredListeners) {
      listener.handler();
    }
  });
}

function ensureGlobalOutsideHandlers(phase: OutsideClickPhase) {
  if (typeof document === 'undefined') return;
  if (globalOutsideHandlers.some((entry) => entry.phase === phase)) return;
  const downEvents = pointerEventsSupported
    ? ['pointerdown']
    : ['mousedown', 'touchstart'];
  downEvents.forEach((eventName) => {
    const handler = (event: Event) => handleGlobalPointerDown(event, phase);
    addEvent(document, eventName, handler, { capture: phase === 'capture' });
    globalOutsideHandlers.push({ eventName, handler, phase });
  });
}

function tearDownGlobalOutsideHandlers(phase: OutsideClickPhase) {
  if (typeof document === 'undefined') return;
  for (let index = globalOutsideHandlers.length - 1; index >= 0; index -= 1) {
    const entry = globalOutsideHandlers[index];
    if (entry.phase !== phase) continue;
    removeEvent(document, entry.eventName, entry.handler, {
      capture: phase === 'capture'
    });
    globalOutsideHandlers.splice(index, 1);
  }
}

function registerOutsideClickListener(
  listener: OutsideClickListener,
  phase: OutsideClickPhase
) {
  outsideClickListeners[phase].add(listener);
  ensureGlobalOutsideHandlers(phase);
}

function unregisterOutsideClickListener(
  listener: OutsideClickListener,
  phase: OutsideClickPhase
) {
  outsideClickListeners[phase].delete(listener);
  if (!outsideClickListeners[phase].size) {
    tearDownGlobalOutsideHandlers(phase);
  }
}

export function useContentState({
  contentType,
  contentId,
  targetKey
}: {
  contentType?: string;
  contentId?: number | string | null;
  targetKey?: string | null;
}) {
  const contentKey =
    (contentType || '') + (contentId || 0) + (targetKey ? `/${targetKey}` : '');
  const state = useContentContext((v) => v.state[contentKey]);
  // Reducers replace a canonical entry when it changes. Preserve the merged
  // snapshot across visibility/height renders so memoized previews can skip.
  return useMemo(
    () => state ? { ...defaultContentState, ...state } : defaultContentState,
    [state]
  );
}

export function useInterval(callback: (v?: any) => any, interval: number) {
  const timerRef = useRef<any>(null);
  useEffect(() => {
    timerRef.current = setInterval(callback, interval);
    return function cleanUp() {
      clearInterval(timerRef.current);
    };
  }, [callback, interval]);
}

export function useLazyLoadForImage(selector: string, setClass: string) {
  useEffect(() => {
    const elements = document.querySelectorAll(selector);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(setClass);
            observer.unobserve(entry.target);
          }
        });
      });

      elements.forEach((element) => {
        observer.observe(element);
      });

      return () => {
        elements.forEach((element) => {
          observer.unobserve(element);
        });
      };
    }
  }, [selector, setClass]);
}

export function useLazyLoad({
  id,
  PanelRef,
  inView,
  onSetPlaceholderHeight,
  delay = 1000
}: {
  id: string;
  PanelRef?: React.RefObject<any>;
  inView: boolean;
  onSetPlaceholderHeight?: (height: number) => void;
  delay?: number;
}): boolean {
  const subscribe = useCallback(
    (callback: () => void) => lazyLoadStore.subscribe(id, callback),
    [id]
  );
  const getSnapshot = useCallback(() => lazyLoadStore.getVisibility(id), [id]);

  const isVisible = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const onSetPlaceholderHeightRef = useRef(onSetPlaceholderHeight);
  const hasPlaceholderHeightCallback = Boolean(onSetPlaceholderHeight);

  useEffect(() => {
    onSetPlaceholderHeightRef.current = onSetPlaceholderHeight;
  }, [onSetPlaceholderHeight]);

  useEffect(() => {
    lazyLoadStore.setVisibility(id, inView, inView ? 0 : delay);
  }, [id, inView, delay]);

  useEffect(() => {
    if (!PanelRef || !hasPlaceholderHeightCallback) return;

    const panelRef = PanelRef;
    let frame = 0;
    let disposed = false;
    let observedElement: Element | null = null;
    const handleResize = throttle((entries: ResizeObserverEntry[]) => {
      // Disconnecting ResizeObserver does not cancel a trailing throttled call.
      // A hidden/replaced panel must not publish its stale height after cleanup.
      if (!disposed && entries.length > 0) {
        const clientHeight = measureOwnerLayout('lazy-placeholder', () => entries[0].target.clientHeight);
        onSetPlaceholderHeightRef.current?.(clientHeight);
      }
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);

    observePanelWhenReady();

    return () => {
      disposed = true;
      if (frame) window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };

    function observePanelWhenReady() {
      const panelElement = panelRef.current;
      if (panelElement && panelElement !== observedElement) {
        if (observedElement) {
          resizeObserver.unobserve(observedElement);
        }
        observedElement = panelElement;
        resizeObserver.observe(panelElement);
        onSetPlaceholderHeightRef.current?.(
          measureOwnerLayout('lazy-placeholder', () => panelElement.clientHeight)
        );
        return;
      }
      if (!panelElement) {
        if (inView || isVisible) {
          frame = window.requestAnimationFrame(observePanelWhenReady);
        }
      }
    }
  }, [PanelRef, hasPlaceholderHeightCallback, id, inView, isVisible]);

  return isVisible;
}

export function useMyState() {
  const contextValues = useAppContext((v) => v.user.state.myState);

  const userId = useAppContext((v) => v.user.state.myState.userId);

  const myStateFromUserObj =
    useAppContext((v) => v.user.state.userObj[userId]) || EMPTY_USER_STATE;

  const missions = useAppContext((v) => v.user.state.missions);
  const notifications = useAppContext(
    (v) => v.user.state.myState.state?.notifications
  );
  const collectType = useAppContext((v) => v.user.state.myState.collectType);
  const lastChatPath = useAppContext((v) => v.user.state.myState.lastChatPath);
  const hideWatched = useAppContext((v) => v.user.state.myState.hideWatched);
  const searchFilter = useAppContext((v) => v.user.state.myState.searchFilter);
  const buildQuickAccessMode = useAppContext(
    (v) => v.user.state.myState.buildQuickAccessMode
  );
  const wordleStrictMode = useAppContext(
    (v) => v.user.state.myState.wordleStrictMode
  );

  const loaded = useAppContext((v) => v.user.state.loaded);
  const sessionInterruption = useAppContext(
    (v) => v.user.state.sessionInterruption
  );
  const signinModalShown = useAppContext((v) => v.user.state.signinModalShown);

  const getStoredItems = (config: { [key: string]: any }) => {
    return Object.keys(config).reduce((acc: { [key: string]: any }, key) => {
      const storedValue = getStoredItem(key, config[key]);
      if (key === 'userId') {
        acc[key] = storedValue ? Number(storedValue) : null;
      } else if (
        key === 'level' ||
        key === 'karmaPoints' ||
        key === 'managementLevel'
      ) {
        acc[key] = Number(storedValue);
      } else {
        acc[key] = storedValue;
      }
      return acc;
    }, {});
  };

  const storedItems = useMemo(
    () => getStoredItems(localStorageKeys),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionInterruption, userId]
  );
  const authIdentityHidden =
    Boolean(sessionInterruption) ||
    hasRejectedAuthSessionMarker() ||
    hasExplicitAuthLogoutMarker();
  const visibleStoredItems = useMemo(
    () =>
      getVisibleCachedIdentity(storedItems, authIdentityHidden),
    [authIdentityHidden, storedItems]
  );
  const result = useMemo(() => {
    return userId && !authIdentityHidden
      ? {
          unlockedAchievementIds: [],
          ...contextValues,
          ...myStateFromUserObj,
          notifications,
          collectType,
          buildQuickAccessMode,
          hideWatched,
          lastChatPath,
          searchFilter,
          wordleStrictMode,
          missions: {
            ...(myStateFromUserObj?.state?.missions || {}),
            ...missions
          },
          isAdmin: myStateFromUserObj.managementLevel >= ADMIN_MANAGEMENT_LEVEL,
          sessionInterruption,
          loggedIn: true
        }
      : {
          loaded,
          unlockedAchievementIds: [],
          missions: {},
          rewardBoostLvl: 0,
          signinModalShown,
          isAdmin:
            Number(visibleStoredItems.managementLevel || 0) >=
            ADMIN_MANAGEMENT_LEVEL,
          ...visibleStoredItems,
          profileTheme:
            visibleStoredItems.profileTheme || DEFAULT_PROFILE_THEME,
          userId: visibleStoredItems.userId || null,
          loggedIn: false,
          sessionInterruption
        };
  }, [
    buildQuickAccessMode,
    authIdentityHidden,
    collectType,
    contextValues,
    hideWatched,
    lastChatPath,
    loaded,
    missions,
    myStateFromUserObj,
    notifications,
    searchFilter,
    sessionInterruption,
    signinModalShown,
    visibleStoredItems,
    userId,
    wordleStrictMode
  ]);

  return result;
}

export function useOutsideClick(
  ref: any,
  callback?: () => any,
  options?: {
    capture?: boolean;
    enabled?: boolean;
    closeOnScroll?: boolean;
    suppressFeedCardNavigation?: boolean;
  }
) {
  const {
    capture = false,
    enabled = true,
    closeOnScroll = false,
    suppressFeedCardNavigation = false
  } = options || {};
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !callback || typeof document === 'undefined') return;
    const refs = Array.isArray(ref) ? ref : [ref];
    const listener: OutsideClickListener = {
      refs,
      handler: () => callbackRef.current?.(),
      suppressFeedCardNavigation
    };
    const phase = capture ? 'capture' : 'bubble';
    registerOutsideClickListener(listener, phase);
    return function cleanup() {
      unregisterOutsideClickListener(listener, phase);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, capture, enabled, suppressFeedCardNavigation]);

  useEffect(() => {
    if (
      !enabled ||
      !closeOnScroll ||
      !callback ||
      typeof document === 'undefined'
    )
      return;
    const refs = Array.isArray(ref) ? ref : [ref];

    function handleScroll(event: Event) {
      const target = event.target as Node | null;
      if (!popupTargetIsOutside(refs, target)) return;
      callbackRef.current?.();
    }

    addEvent(document, 'scroll', handleScroll, { capture: true });
    return function cleanUp() {
      removeEvent(document, 'scroll', handleScroll, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, enabled, closeOnScroll]);
}

export function useProfileState(username: string) {
  const userState = useProfileContext((v) => v.state[username]) || {};
  const notExist = userState.notExist ?? false;
  const notables = userState.notables || DEFAULT_PROFILE_NOTABLES;
  const subjects = userState.subjects || DEFAULT_PROFILE_SUBJECTS;
  const pinnedAICards =
    userState.pinnedAICards || DEFAULT_PROFILE_PINNED_AI_CARDS;
  const pinnedBuilds =
    userState.pinnedBuilds || DEFAULT_PROFILE_PINNED_BUILDS;
  const likes = userState.likes || DEFAULT_PROFILE_LIKES;
  const posts = userState.posts || DEFAULT_PROFILE_POSTS;
  const profileId = userState.profileId;

  return useMemo(
    () => ({
      likes,
      subjects,
      pinnedAICards,
      pinnedBuilds,
      notables,
      posts,
      notExist,
      profileId
    }),
    [
      likes,
      subjects,
      pinnedAICards,
      pinnedBuilds,
      notables,
      posts,
      notExist,
      profileId
    ]
  );
}

export function useSearch({
  onSearch,
  onEmptyQuery,
  onClear,
  onSetSearchText
}: {
  onSearch: (text: string) => any;
  onEmptyQuery?: () => void;
  onClear?: () => void;
  onSetSearchText: (text: string) => void;
}) {
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchGenerationRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  function handleSearch(text: string) {
    const searchGeneration = ++searchGenerationRef.current;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onSetSearchText(text);
    onClear?.();
    if (stringIsEmpty(text)) {
      onEmptyQuery?.();
      if (isMountedRef.current) {
        setSearching(false);
      }
      return;
    }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      try {
        await onSearch(text);
      } catch (error) {
        console.error('Search request failed:', error);
      } finally {
        if (
          isMountedRef.current &&
          searchGeneration === searchGenerationRef.current
        ) {
          setSearching(false);
          timerRef.current = null;
        }
      }
    }, 500);
  }

  return { handleSearch, searching };
}

export function useMyLevel() {
  const achievementPoints = useKeyContext((v) => v.myState.achievementPoints);
  const canEdit = useKeyContext((v) => v.myState.canEdit);
  const canDelete = useKeyContext((v) => v.myState.canDelete);
  const canReward = useKeyContext((v) => v.myState.canReward);
  const canPinPlaylists = useKeyContext((v) => v.myState.canPinPlaylists);
  const canEditRewardLevel = useKeyContext((v) => v.myState.canEditRewardLevel);
  const managementLevel = useKeyContext((v) => v.myState.managementLevel);

  const result = useMemo(() => {
    for (let i = levels.length - 1; i >= 0; i--) {
      if (achievementPoints >= levels[i].ap) {
        return {
          ...levels[i],
          canEdit: canEdit || levels[i].canEdit,
          canDelete: canDelete || levels[i].canDelete,
          canReward: canReward || levels[i].canReward,
          canPinPlaylists: canPinPlaylists || levels[i].canPinPlaylists,
          canEditRewardLevel:
            canEditRewardLevel || levels[i].canEditRewardLevel,
          managementLevel: Math.max(managementLevel, levels[i].managementLevel),
          nextLevelAp: i === levels.length - 1 ? null : levels[i + 1].ap
        };
      }
    }
    return {
      ...levels[1],
      canEdit: canEdit || levels[1].canEdit,
      canDelete: canDelete || levels[1].canDelete,
      canReward: canReward || levels[1].canReward,
      canPinPlaylists: canPinPlaylists || levels[1].canPinPlaylists,
      canEditRewardLevel: canEditRewardLevel || levels[1].canEditRewardLevel,
      managementLevel: Math.max(managementLevel, levels[1].managementLevel),
      nextLevelAp: levels[2].ap
    };
  }, [
    achievementPoints,
    canDelete,
    canEdit,
    canEditRewardLevel,
    canPinPlaylists,
    canReward,
    managementLevel
  ]);

  return result;
}

export function useUserLevel(user: User): UserLevel {
  const userData = useAppContext((v) => v.user.state.userObj[user.id]);
  const {
    achievementPoints = user.achievementPoints || 0,
    canEdit,
    canDelete,
    canReward,
    canPinPlaylists,
    canEditRewardLevel,
    managementLevel = 0
  } = userData || {};

  const result = useMemo(() => {
    for (let i = levels.length - 1; i >= 0; i--) {
      if (achievementPoints >= levels[i].ap) {
        return {
          ...levels[i],
          canEdit: canEdit || levels[i].canEdit,
          canDelete: canDelete || levels[i].canDelete,
          canReward: canReward || levels[i].canReward,
          canPinPlaylists: canPinPlaylists || levels[i].canPinPlaylists,
          canEditRewardLevel:
            canEditRewardLevel || levels[i].canEditRewardLevel,
          managementLevel: Math.max(managementLevel, levels[i].managementLevel),
          nextLevelAp: i === levels.length - 1 ? null : levels[i + 1].ap
        };
      }
    }
    return {
      ...levels[1],
      canEdit: canEdit || levels[1].canEdit,
      canDelete: canDelete || levels[1].canDelete,
      canReward: canReward || levels[1].canReward,
      canPinPlaylists: canPinPlaylists || levels[1].canPinPlaylists,
      canEditRewardLevel: canEditRewardLevel || levels[1].canEditRewardLevel,
      managementLevel: Math.max(managementLevel, levels[1].managementLevel),
      nextLevelAp: levels[2].ap
    };
  }, [
    achievementPoints,
    canDelete,
    canEdit,
    canEditRewardLevel,
    canPinPlaylists,
    canReward,
    managementLevel
  ]);

  return result;
}

export function useWordleLabels({
  isSolved,
  isStrict,
  numGuesses,
  solution,
  wordLevel,
  wordLevelColor,
  xpRewardAmount,
  username,
  userId,
  myId
}: {
  isSolved: boolean;
  isStrict: boolean;
  numGuesses: number;
  solution: string;
  wordLevel: number;
  wordLevelColor?: string;
  xpRewardAmount: number;
  username: string;
  userId: number;
  myId: number;
}) {
  const displayedUserLabel = useMemo(() => {
    if (userId === myId) {
      return 'You';
    }
    return (
      <UsernameText
        color="#fff"
        user={{
          id: userId,
          username
        }}
      />
    );
  }, [myId, userId, username]);

  const rewardAmountLabel = useMemo(
    () => addCommasToNumber(xpRewardAmount),
    [xpRewardAmount]
  );

  const solutionLabel = useMemo(
    () => (
      <>
        The word was <b>{solution}</b>{' '}
        <span style={{ whiteSpace: 'nowrap' }}>
          (
          <b
            style={{
              color:
                wordLevelColor ||
                Color[wordLevelHash[wordLevel]?.color || 'logoBlue']()
            }}
          >
            {wordLevelHash[wordLevel]?.label || ''}
          </b>{' '}
          word)
        </span>
      </>
    ),
    [solution, wordLevel, wordLevelColor]
  );

  const guessLabel = useMemo(() => {
    if (wordleGuessReaction[numGuesses]) {
      return wordleGuessReaction[numGuesses];
    }
    return null;
  }, [numGuesses]);

  const guessLabelColor = useMemo(
    () =>
      numGuesses <= 2
        ? Color.gold()
        : numGuesses === 3
        ? Color.brownOrange()
        : Color.orange(),
    [numGuesses]
  );

  const bonusLabel = useMemo(() => {
    if (numGuesses < 3) {
      return null;
    }
    return isSolved && isStrict ? 'double reward bonus' : null;
  }, [isSolved, isStrict, numGuesses]);

  const resultLabel = useMemo(() => {
    return (
      <>
        {' '}
        {displayedUserLabel} earned{' '}
        <span
          className="reward-amount-label"
          style={{
            fontWeight: isSolved ? 'bold' : ''
          }}
        >
          {rewardAmountLabel} XP
        </span>{' '}
        for {isSolved ? 'solving' : 'trying to solve'} a Wordle{' '}
        {isSolved ? (
          <>
            in{' '}
            <span style={{ fontWeight: numGuesses <= 4 ? 'bold' : 'default' }}>
              {numGuesses} guess
              {numGuesses === 1
                ? '!!!'
                : numGuesses === 2
                ? 'es!!'
                : numGuesses === 3
                ? 'es!'
                : 'es'}
            </span>
          </>
        ) : (
          ''
        )}
      </>
    );
  }, [displayedUserLabel, isSolved, numGuesses, rewardAmountLabel]);

  return {
    bonusLabel,
    guessLabel,
    guessLabelColor,
    resultLabel,
    solutionLabel
  };
}
